import { useState } from "react";
import {
  Box,
  Heading,
  Flex,
  Icon,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Text,
  SimpleGrid,
  Image,
  useToast,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { IoIosArrowBack } from "react-icons/io";
import { HiMail } from "react-icons/hi";
import { RiLockFill } from "react-icons/ri";
import { FaUser, FaPhoneAlt } from "react-icons/fa";

import { MobileHeader } from "../components/MobileHeader";
import { EmailVerificationModal } from "../components/EmailVerificationModal";
import { signUpApi, sendEmailOtpApi } from "../services/auth";
import Intro2 from "../assets/images/intro2.gif";

const COUNTRY_CODES = [
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+353", label: "🇮🇪 +353" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+234", label: "🇳🇬 +234" },
  { code: "+27", label: "🇿🇦 +27" },
  { code: "+254", label: "🇰🇪 +254" },
  { code: "+233", label: "🇬🇭 +233" },
  { code: "+49", label: "🇩🇪 +49" },
  { code: "+33", label: "🇫🇷 +33" },
  { code: "+971", label: "🇦🇪 +971" },
];

export const Signup = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>("+44");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [signupToken, setSignupToken] = useState<string>("");

  const calculatedHeight = `calc(100vh - 72px)`;

  const showToast = (
    title: string,
    description: string,
    status: "success" | "error"
  ) => {
    toast({
      title,
      description,
      status,
      duration: 3000,
      isClosable: true,
      position: "top-right",
    });
  };

  const handleSubmit = async () => {
    if (!email || !password || !confirmPassword) {
      showToast("An error occurred", "Please fill in all fields", "error");
      return;
    }

    if (password !== confirmPassword) {
      showToast("An error occurred", "Passwords do not match", "error");
      return;
    }

    if (password.length < 8) {
      showToast(
        "An error occurred",
        "Password must be at least 8 characters",
        "error"
      );
      return;
    }

    setLoading(true);

    const normalizedEmail = email.toLocaleLowerCase();

    const res = await signUpApi({
      email: normalizedEmail,
      password,
      user_type: "client",
      scope_type: "individual",
      first_name: firstName.trim() || undefined,
      last_name: lastName.trim() || undefined,
      phone: phone.trim() || undefined,
      country_code: phone.trim() ? countryCode : undefined,
    });

    if (res && !res.error) {
      const token = res.token;
      const sentOtpResponse = await sendEmailOtpApi(normalizedEmail, token);

      if (sentOtpResponse && !sentOtpResponse.error) {
        setSignupToken(token);
        onOpen();
      } else {
        showToast(
          "An error occurred",
          sentOtpResponse?.message ||
            "Could not send verification code. Please try again.",
          "error"
        );
      }
    } else {
      showToast(
        "An error occurred",
        res?.message || "Could not create your account. Please try again.",
        "error"
      );
    }

    setLoading(false);
  };

  return (
    <Box>
      <MobileHeader title="Sign Up" />

      <Flex
        boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
        h="72px"
        px="10%"
        align={"center"}
        justify={"space-between"}
        display={{ base: "none", md: "flex" }}
      >
        <Icon fontSize={24} color={"#19202D"} onClick={() => navigate(-1)}>
          <IoIosArrowBack />
        </Icon>

        <Button
          bg={"#FF8E3C"}
          _hover={{ bg: "#FF8E3C" }}
          borderRadius={"12px"}
          color={"white"}
          h="48px"
          fontSize={"15px"}
          lineHeight={"18.2px"}
          onClick={() => navigate("/login")}
          fontFamily={"RedHatDisplay-Bold"}
        >
          Login
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }}>
        <Flex
          bg={"linear-gradient(to bottom right, #FFF7F3, #D8F1F2)"}
          h={{ base: "auto", md: calculatedHeight }}
          borderBottomLeftRadius={{ base: 24, md: 0 }}
          borderBottomRightRadius={{ base: 24, md: 0 }}
          px={{ base: "24px", md: "10%" }}
          pt={{ base: "24px", md: 0 }}
          flexDir={"column"}
          justify={"center"}
          display={{ base: "none", md: "flex" }}
        >
          <Flex
            justify={"center"}
            align={"center"}
            h={"100%"}
            display={{ base: "none", md: "flex" }}
          >
            <Image src={Intro2} alt="bookd-intro" objectFit={"cover"} />
          </Flex>
        </Flex>

        <Flex
          pt={{ base: "24px", md: 0 }}
          pb={{ base: "24px", md: 0 }}
          flexDir={"column"}
          justify={"center"}
          px={{ base: "20px", md: "10%" }}
        >
          <Heading
            fontSize={"32px"}
            color={"#19202D"}
            textAlign={"center"}
            fontFamily={"RedHatDisplay-Bold"}
            display={{ base: "none", md: "block" }}
          >
            Sign Up
          </Heading>

          <Text
            color={"#4F4F51"}
            fontSize={{ base: "14px", md: "20px" }}
            textAlign={"center"}
            mt={"7px"}
            px="10%"
            display={{ base: "none", md: "block" }}
            mb="32px"
          >
            Create an account to book beauty services and manage your
            appointments
          </Text>

          <Box mb="20px">
            <Heading
              fontSize={"16px"}
              color={"#19202D"}
              mb="8px"
              fontFamily={"RedHatDisplay-Bold"}
            >
              First Name
            </Heading>

            <InputGroup
              borderRadius={"40px"}
              h="56px"
              alignItems={"center"}
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
            >
              <InputLeftElement pointerEvents="none" h="56px" pl="16px">
                <Icon fontSize={20} color={"#9BA3A3"}>
                  <FaUser />
                </Icon>
              </InputLeftElement>

              <Input
                placeholder="Enter Your First Name"
                borderWidth={0}
                fontSize={"16px"}
                ml="8px"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                borderColor={"transparent"}
                _focusVisible={{ borderColor: "none" }}
              />
            </InputGroup>
          </Box>

          <Box mb="20px">
            <Heading
              fontSize={"16px"}
              color={"#19202D"}
              mb="8px"
              fontFamily={"RedHatDisplay-Bold"}
            >
              Last Name
            </Heading>

            <InputGroup
              borderRadius={"40px"}
              h="56px"
              alignItems={"center"}
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
            >
              <InputLeftElement pointerEvents="none" h="56px" pl="16px">
                <Icon fontSize={20} color={"#9BA3A3"}>
                  <FaUser />
                </Icon>
              </InputLeftElement>

              <Input
                placeholder="Enter Your Last Name"
                borderWidth={0}
                fontSize={"16px"}
                ml="8px"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                borderColor={"transparent"}
                _focusVisible={{ borderColor: "none" }}
              />
            </InputGroup>
          </Box>

          <Box mb="20px">
            <Heading
              fontSize={"16px"}
              color={"#19202D"}
              mb="8px"
              fontFamily={"RedHatDisplay-Bold"}
            >
              Phone Number
            </Heading>

            <Flex gap="8px">
              <Select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                w="120px"
                h="56px"
                borderRadius={"40px"}
                borderWidth={0}
                fontSize={"15px"}
                boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
                _focusVisible={{ borderColor: "none", boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)" }}
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </Select>

              <InputGroup
                borderRadius={"40px"}
                h="56px"
                flex={1}
                alignItems={"center"}
                boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
              >
                <InputLeftElement pointerEvents="none" h="56px" pl="16px">
                  <Icon fontSize={18} color={"#9BA3A3"}>
                    <FaPhoneAlt />
                  </Icon>
                </InputLeftElement>

                <Input
                  type="tel"
                  placeholder="Enter Your Phone Number"
                  borderWidth={0}
                  fontSize={"16px"}
                  ml="8px"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  borderColor={"transparent"}
                  _focusVisible={{ borderColor: "none" }}
                />
              </InputGroup>
            </Flex>
          </Box>

          <Box mb="20px">
            <Heading
              fontSize={"16px"}
              color={"#19202D"}
              mb="8px"
              fontFamily={"RedHatDisplay-Bold"}
            >
              Email
            </Heading>

            <InputGroup
              borderRadius={"40px"}
              h="56px"
              alignItems={"center"}
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
            >
              <InputLeftElement pointerEvents="none" h="56px" pl="16px">
                <Icon fontSize={24} color={"#9BA3A3"}>
                  <HiMail />
                </Icon>
              </InputLeftElement>

              <Input
                placeholder="Enter Your Email"
                type="email"
                borderWidth={0}
                fontSize={"16px"}
                ml="8px"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                borderColor={"transparent"}
                _focusVisible={{ borderColor: "none" }}
              />
            </InputGroup>
          </Box>

          <Box mb="20px">
            <Heading
              fontSize={"16px"}
              color={"#19202D"}
              mb="8px"
              fontFamily={"RedHatDisplay-Bold"}
            >
              Password
            </Heading>

            <InputGroup
              borderRadius={"40px"}
              h="56px"
              alignItems={"center"}
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
            >
              <InputLeftElement pointerEvents="none" h="56px" pl="16px">
                <Icon fontSize={24} color={"#9BA3A3"}>
                  <RiLockFill />
                </Icon>
              </InputLeftElement>

              <Input
                type="password"
                placeholder="******"
                borderWidth={0}
                fontSize={"16px"}
                ml="8px"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                borderColor={"transparent"}
                _focusVisible={{ borderColor: "none" }}
              />
            </InputGroup>
          </Box>

          <Box mb="10px">
            <Heading
              fontSize={"16px"}
              color={"#19202D"}
              mb="8px"
              fontFamily={"RedHatDisplay-Bold"}
            >
              Confirm Password
            </Heading>

            <InputGroup
              borderRadius={"40px"}
              h="56px"
              alignItems={"center"}
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
            >
              <InputLeftElement pointerEvents="none" h="56px" pl="16px">
                <Icon fontSize={24} color={"#9BA3A3"}>
                  <RiLockFill />
                </Icon>
              </InputLeftElement>

              <Input
                type="password"
                placeholder="******"
                borderWidth={0}
                fontSize={"16px"}
                ml="8px"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                borderColor={"transparent"}
                _focusVisible={{ borderColor: "none" }}
              />
            </InputGroup>
          </Box>

          <Button
            bg={"#FF8E3C"}
            _hover={{ bg: "#FF8E3C" }}
            mt="24px"
            borderRadius={"30px"}
            color={"white"}
            h="58px"
            fontSize={"17px"}
            fontFamily={"RedHatDisplay-Bold"}
            onClick={handleSubmit}
            mx={{ base: 0, md: "15%" }}
            disabled={loading}
            isLoading={loading}
            loadingText={"Please Wait"}
          >
            Sign Up
          </Button>
        </Flex>
      </SimpleGrid>

      <EmailVerificationModal
        isOpen={isOpen}
        onClose={onClose}
        email={email.toLocaleLowerCase()}
        token={signupToken}
        onVerified={() => {
          onClose();
          navigate("/login");
        }}
      />
    </Box>
  );
};
